// ============================================================
// Raya AI — marketing page content
// The point of this page: Raya AI does the work, it does not
// just describe it. Every task below ends in a real record.
// ============================================================

import { RATES } from '../pricing/pricingData';

export const AI_PRICE = RATES.ai;

// What one seat buys. Both counters are company-wide and shared, they reset on
// the 1st and they do not roll over — the same figures the Usage screen shows.
export const SEAT_QUESTIONS = 100;
export const SEAT_ACTIONS = 5;

// ─── What it can be asked to do ─────────────────────────────────────────────
export const CAPABILITY_GROUPS = [
  {
    key: 'record',
    title: 'Record',
    blurb: 'Dictate the document and AI fills it in, coded and taxed correctly.',
    icon: 'receipt-indian-rupee',
    color: '#2563eb',
    tint: '#eff6ff',
    items: ['Purchase bills against a PO', 'Expenses with the right ledger', 'Sales and cash receipts', 'Payments and settlements'],
  },
  {
    key: 'create',
    title: 'Create',
    blurb: 'AI drafts it from your masters, rates and history.',
    icon: 'file-plus-2',
    color: '#4f46e5',
    tint: '#eef2ff',
    items: ['Quotations at the customer’s rate', 'Sales and purchase orders', 'Work orders from a BOM', 'Follow-up emails that read like you'],
  },
  {
    key: 'find',
    title: 'Find',
    blurb: 'One question instead of four screens and a filter.',
    icon: 'package-open',
    color: '#0891b2',
    tint: '#ecfeff',
    items: ['Stock across every warehouse', 'What a customer last paid', 'Invoices behind on payment', 'Any document, by what you remember'],
  },
  {
    key: 'foresee',
    title: 'Foresee',
    blurb: 'AI reads your own history and says what is coming.',
    icon: 'line-chart',
    color: '#059669',
    tint: '#ecfdf5',
    items: ['Demand and reorder forecasts', 'Cash position weeks ahead', 'Items about to stock out', 'Customers drifting away'],
  },
];

// ─── The worked conversations ───────────────────────────────────────────────
// Each is a real request, the steps Raya AI takes, and the record it leaves.
export const AI_TASKS = [
  {
    key: 'bill',
    label: 'Record a bill',
    icon: 'receipt-indian-rupee',
    color: '#2563eb',
    tint: '#eff6ff',
    prompt: 'Book the bill from Shakti Steels for ₹1,00,000 plus GST against PO-2291.',
    steps: [
      'Found vendor Shakti Steels · GSTIN 24AAACS…',
      'Matched PO-2291 and the GRN — three-way check clean',
      'Applied GST at 18% as CGST 9% + SGST 9%',
      'Filled the voucher and sent it for your approval',
    ],
    result: {
      kind: 'doc',
      title: 'Purchase Bill · draft',
      badge: ['Needs your approval', 'info'],
      fields: [
        ['Vendor', 'Shakti Steels'],
        ['Against', 'PO-2291 · GRN-1180'],
        ['Taxable value', '₹1,00,000'],
        ['CGST 9% + SGST 9%', '₹18,000'],
      ],
      total: ['Bill total', '₹1,18,000'],
      note: 'Nothing is saved until you approve it. One tap posts it to Purchases.',
    },
  },
  {
    key: 'expense',
    label: 'Log an expense',
    icon: 'wallet',
    color: '#d97706',
    tint: '#fffbeb',
    prompt: 'Log ₹4,200 of diesel for the Ahmedabad delivery van, paid in cash.',
    steps: [
      'Coded to Vehicle Running · Fuel',
      'Tagged cost centre Ahmedabad Logistics',
      'Matched the asset: van GJ-01-KL-4429',
      'Drew it against the petty cash account',
    ],
    result: {
      kind: 'doc',
      title: 'Expense voucher · draft',
      badge: ['Needs your approval', 'info'],
      fields: [
        ['Head', 'Vehicle Running · Fuel'],
        ['Cost centre', 'Ahmedabad Logistics'],
        ['Asset', 'GJ-01-KL-4429'],
        ['Paid from', 'Petty cash'],
      ],
      total: ['Amount', '₹4,200'],
      note: 'Attach the slip from your phone, approve, and it posts against this entry.',
    },
  },
  {
    key: 'sale',
    label: 'Record a sale',
    icon: 'indian-rupee',
    color: '#059669',
    tint: '#ecfdf5',
    prompt: 'Sold 40 boxes of 12mm TMT to Patel Traders at their usual rate. Invoice it.',
    steps: [
      'Pulled Patel Traders’ contracted rate · ₹2,450 a box',
      'Checked 40 boxes are free in Warehouse A',
      'Worked out the GST and filled the invoice',
      'Queued IRN + QR and the e-Way Bill for the moment you approve',
    ],
    result: {
      kind: 'doc',
      title: 'Tax Invoice · draft',
      badge: ['Needs your approval', 'info'],
      fields: [
        ['Customer', 'Patel Traders'],
        ['Item', '12mm TMT · 40 boxes'],
        ['Rate', '₹2,450 a box'],
        ['GST 18%', '₹17,640'],
      ],
      total: ['Invoice total', '₹1,15,640'],
      note: 'Approve it and the IRN, the e-Way Bill and the stock movement all follow.',
    },
  },
  {
    key: 'quote',
    label: 'Draft a quotation',
    icon: 'file-text',
    color: '#4f46e5',
    tint: '#eef2ff',
    prompt: 'Quote Meera Industries for 200 flanges, same terms as their last order.',
    steps: [
      'Read their last order · QT-6610, closed in March',
      'Carried forward 30-day credit and door delivery',
      'Applied the 4% slab discount at 200 units',
      'Filled the quotation, ready for you to read',
    ],
    result: {
      kind: 'doc',
      title: 'Quotation · draft',
      badge: ['Needs your approval', 'info'],
      fields: [
        ['Customer', 'Meera Industries'],
        ['Item', 'MS Flange 4" · 200 nos'],
        ['Rate after 4% slab', '₹612 each'],
        ['Terms', '30 days · door delivery'],
      ],
      total: ['Quotation value', '₹1,22,400'],
      note: 'Nothing goes out until you read it and press send.',
    },
  },
  {
    key: 'stock',
    label: 'Find inventory',
    icon: 'package',
    color: '#0891b2',
    tint: '#ecfeff',
    prompt: 'How many 12mm TMT boxes do we have, and where?',
    steps: [
      'Searched every warehouse and in-transit lot',
      'Netted off what is reserved against open orders',
      'Checked reorder levels against the reorder point',
    ],
    result: {
      kind: 'table',
      title: '12mm TMT · stock on hand',
      badge: ['Live', 'ok'],
      cols: ['Location', 'On hand', 'Free'],
      rows: [
        ['Warehouse A · Ahmedabad', '184 boxes', '144'],
        ['Warehouse B · Rajkot', '96 boxes', '96'],
        ['In transit · GRN pending', '60 boxes', '—'],
      ],
      total: ['Free to sell', '240 boxes'],
      note: 'Warehouse B is 24 boxes under its reorder point.',
    },
  },
  {
    key: 'forecast',
    label: 'Forecast demand',
    icon: 'line-chart',
    color: '#7c3aed',
    tint: '#f5f3ff',
    prompt: 'What will 12mm TMT sell next month, and do I need to order?',
    steps: [
      'Read 14 months of sales for this item',
      'Adjusted for the festive dip and two open contracts',
      'Compared the forecast against free stock and lead time',
    ],
    result: {
      kind: 'table',
      title: '12mm TMT · next 30 days',
      badge: ['Forecast', 'info'],
      cols: ['Signal', 'Value', ''],
      rows: [
        ['Expected demand', '310 boxes', '±8%'],
        ['Free stock today', '240 boxes', ''],
        ['Shortfall', '70 boxes', 'by day 24'],
      ],
      total: ['Suggested order', '120 boxes'],
      note: 'Vendor lead time is 9 days — raise the indent by the 15th.',
    },
  },
];

// ─── Guardrails ─────────────────────────────────────────────────────────────
// Enterprise buyers ask this before they ask anything else.
export const AI_GUARDRAILS = [
  { title: 'AI works inside your permissions', body: 'AI can only see and touch what the person asking is already allowed to. There is no back door of its own.', icon: 'key-round' },
  { title: 'Nothing saves without a person', body: 'The assistant prepares the voucher, quotation or order and stops. A person approves before anything is written to your books — and you can cap the value it is allowed to draft at all.', icon: 'clipboard-check' },
  { title: 'Every action is on the record', body: 'Each entry is stamped with the prompt behind it and the person who approved it, so the audit trail shows what was asked, what AI did and who signed it off.', icon: 'shield-check' },
  { title: 'AI runs in India', body: 'Requests are served from Indian infrastructure, and your books are never used to train anyone’s model. Prompt logs are off by default, and PAN, Aadhaar, bank and phone identifiers are masked before anything is written down.', icon: 'landmark' },
  { title: 'AI says when it is unsure', body: 'If the vendor is ambiguous or the rate has moved, AI asks rather than guessing and quietly filling in the wrong thing.', icon: 'info' },
  { title: 'Undo is one step', body: 'Anything approved can be reversed the same way anything else is, with the reversal carrying the same audit trail.', icon: 'refresh-cw' },
];

// ─── What an admin can see and set ──────────────────────────────────────────
export const AI_CONTROLS = [
  {
    key: 'usage',
    tab: 'Usage',
    title: 'See exactly what it is being used for',
    blurb: 'Questions asked, actions taken and spend — for the company and for every user by name. Broken down per day, over 7, 30 or 90 days.',
    icon: 'bar-chart-3',
    points: [
      'Questions and actions used against your pool',
      'Spend per user, and what caching saved you',
      'Time it took off your team, in hours',
      'Pool resets on the 1st · no rollover, no surprise bill',
    ],
  },
  {
    key: 'seats',
    tab: 'Settings › Plan',
    title: 'Give a seat, take a seat, any month',
    blurb: `Seats are assigned by you, not by us. Each one adds ${SEAT_QUESTIONS} questions and ${SEAT_ACTIONS} actions to the company’s shared pool.`,
    icon: 'users',
    points: [
      `₹${RATES.ai} per seat, per month`,
      'Add or remove a seat from one month to the next',
      'Turn drafting off entirely, or cap the value it may draft',
      'Top up with a pack when a month runs heavy',
    ],
  },
  {
    key: 'privacy',
    tab: 'Settings › Privacy',
    title: 'Decide what is written down',
    blurb: 'Prompt logging is off by default. If you switch it on for review, you set how long it is kept and identifiers are still masked.',
    icon: 'shield-check',
    points: [
      'Keep prompt logs: off unless you turn it on',
      'Your own retention window, in days',
      'PAN, Aadhaar, bank and phone numbers masked',
      'HR and Payroll prompts are never logged',
    ],
  },
  {
    key: 'access',
    tab: 'Settings › Access',
    title: 'Switch it on, off, or partly on',
    blurb: 'Raya AI can be turned off for the whole company with one toggle. Left on, each user still only sees what their own role allows.',
    icon: 'key-round',
    points: [
      'One switch for the entire company',
      'Per-user permissions still apply to every answer',
      'Choose which model provider serves your requests',
      'Knowledge, memory and connectors are yours to configure',
    ],
  },
];

export const AI_FAQS = [
  { q: 'Does Raya AI actually create records, or just answer questions?', a: 'It does the work of creating them. Ask it to book a bill, log an expense, invoice a sale or quote a customer and it finds the vendor or customer, matches the PO, works out the GST and fills the document in full — then puts it in front of you. A person approves before anything is saved to your books, which is the part most teams want kept human.' },
  { q: 'What stops it from posting something wrong?', a: 'It cannot post at all on its own — a person approves every document before it is saved. On top of that it runs inside the asking user’s permissions, it asks instead of guessing when a vendor, rate or ledger is ambiguous, and you can cap the value it is allowed to draft or switch drafting off entirely. Everything approved is audited and reversible.' },
  { q: 'Can I see why it did what it did?', a: 'Yes. Every document it prepares carries the steps it took and the masters it matched, and the approval is stamped with who gave it, so an auditor can follow the whole chain without asking anyone to remember.' },
  { q: 'Is my data used to train a model?', a: 'No. Your books are never used to train a model — ours or a provider’s. Answering a request means sending the prompt and the records it needs to our AI provider under a contract that forbids training on it and requires deletion after processing. The subprocessors involved are named in our privacy policy.' },
  { q: 'Do I need it on every user?', a: `No. Seats are ₹${RATES.ai} each per month and are usually given only to the owners and the main operating accounts — you assign and remove them yourself from Settings.` },
  { q: 'What does one AI seat actually include?', a: `Each seat adds ${SEAT_QUESTIONS} questions and ${SEAT_ACTIONS} actions to a pool the whole company shares, so a heavy user and a light one balance each other out. The pool resets on the 1st and does not roll over. If a month runs heavy you can add a top-up pack rather than buying a seat you do not need.` },
  { q: 'Can I see what it is costing and who is using it?', a: 'Yes. The Usage screen shows questions, actions and spend for the company and for each user by name, per day, over the last 7, 30 or 90 days — along with how much caching saved you and how much of your pool is left.' },
  { q: 'Can I turn it off?', a: 'Completely, with one toggle for the whole company. You can also leave it on but switch drafting off, so it answers questions without preparing documents, or cap the value of any document it is allowed to draft.' },
  { q: 'Which modules does it work across?', a: 'All of them. Because Raya runs on one database, a single request can touch inventory, sales, purchase, finance and production without anything being exported or re-keyed.' },
];
